// apps/admin/src/pages/MonitoringPage.tsx

import React, { useState, useEffect } from 'react';
import { useAdminAuditLog } from '../hooks/useAdminAuditLog';
import { GoldenSignalsDashboard } from '../components/monitoring/GoldenSignals';
import { ValidationHealthDashboard } from '../components/monitoring/ValidationHealthDashboard';
import { EmergencyRebuildPanel } from '../components/monitoring/EmergencyRebuildPanel';
import { RuntimeErrorsTab } from '../components/monitoring/RuntimeErrorsTab';
import { OnboardingHealthTab } from '../components/monitoring/OnboardingHealthTab';
import type { ValidationStatus } from '../types/validation';
import { UploadHealthTab } from '../components/monitoring/UploadHealthTab';
import { Activity, Database, AlertTriangle, AlertCircle, Users, Upload } from 'lucide-react';

export default function MonitoringPage() {
  const { logAction } = useAdminAuditLog();

  // Add runtime-errors and onboarding to the tab type
  type TabType = 'infrastructure' | 'validation' | 'rebuild' | 'runtime-errors' | 'onboarding' | 'upload-health';

  const [activeTab, setActiveTab] = useState<TabType>('infrastructure');
  const [validationStatuses, setValidationStatuses] = useState<ValidationStatus[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // Log monitoring dashboard view
  useEffect(() => {
    void logAction({
      action: 'ADMIN_MONITORING_DASHBOARD_VIEWED',
      resource: 'monitoring',
      resourceName: 'System Monitoring Dashboard',
      metadata: {
        view: 'monitoring_dashboard',
      },
    });
  }, [logAction]);

  const handleRebuildComplete = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleValidationDataLoad = (statuses: ValidationStatus[]) => {
    setValidationStatuses(statuses);
  };

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl mb-6">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 opacity-90"></div>
        <div className="relative px-8 py-12">
          <h1 className="text-4xl font-bold text-white mb-3">System Monitoring</h1>
          <p className="text-white opacity-80 text-lg">
            Real-time infrastructure health, validation status, runtime errors, onboarding health, and aggregate management
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 p-1 bg-slate-800/50 backdrop-blur-sm rounded-xl w-fit border border-slate-700/50">
        <button
          onClick={() => setActiveTab('infrastructure')}
          className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
            activeTab === 'infrastructure'
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
          }`}
        >
          <Activity className="w-4 h-4" />
          Infrastructure
        </button>
        
        <button
          onClick={() => setActiveTab('validation')}
          className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
            activeTab === 'validation'
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
          }`}
        >
          <Database className="w-4 h-4" />
          Validation Status
        </button>
        
        <button
          onClick={() => setActiveTab('rebuild')}
          className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
            activeTab === 'rebuild'
              ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
          }`}
        >
          <AlertTriangle className="w-4 h-4" />
          Emergency Rebuild
        </button>

        <button
          onClick={() => setActiveTab('runtime-errors')}
          className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
            activeTab === 'runtime-errors'
              ? 'bg-gradient-to-r from-red-600 to-pink-600 text-white shadow-lg'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
          }`}
        >
          <AlertCircle className="w-4 h-4" />
          Runtime Errors
        </button>

        <button
          onClick={() => setActiveTab('onboarding')}
          className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
            activeTab === 'onboarding'
              ? 'bg-gradient-to-r from-indigo-600 to-cyan-600 text-white shadow-lg'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
          }`}
        >
          <Users className="w-4 h-4" />
          Onboarding Health
        </button>

        <button
          onClick={() => setActiveTab('upload-health')}
          className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
            activeTab === 'upload-health'
              ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg'
              : 'text-slate-400 hover:text-white hover:bg-slate-700/30'
          }`}
        >
          <Upload className="w-4 h-4" />
          Upload Health
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'infrastructure' && <GoldenSignalsDashboard />}

      {activeTab === 'validation' && (
        <ValidationHealthDashboard key={refreshKey} onDataLoad={handleValidationDataLoad} />
      )}

      {activeTab === 'rebuild' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <ValidationHealthDashboard key={refreshKey} onDataLoad={handleValidationDataLoad} />
          </div>
          <div className="lg:col-span-1">
            <EmergencyRebuildPanel
              statuses={validationStatuses}
              onRebuildComplete={handleRebuildComplete}
            />
          </div>
        </div>
      )}

      {activeTab === 'runtime-errors' && <RuntimeErrorsTab />}

      {activeTab === 'onboarding' && <OnboardingHealthTab />}

      {activeTab === 'upload-health' && <UploadHealthTab />}
    </div>
  );
}